import React, { useState, useEffect, useRef, useMemo } from 'react';
import { SearchIcon, XIcon, ChevronDownIcon } from 'lucide-react';
import type { MetroLineOption } from '../../services/hhDictionariesService';

interface SearchableMetroSelectProps {
  value: string | string[];
  onChange: (stationIds: string | string[]) => void;
  lines: MetroLineOption[];
  placeholder?: string;
  className?: string;
  compact?: boolean;
  multiple?: boolean;
  accentColor?: string;
}

export const SearchableMetroSelect: React.FC<SearchableMetroSelectProps> = ({
  value,
  onChange,
  lines,
  placeholder = 'Например: м. Парк Победы',
  className = '',
  compact = false,
  multiple = false,
  accentColor,
}) => {
  const accent = accentColor || '#3b82f6';
  const [isOpen, setIsOpen] = useState(false);
  const [query, setQuery] = useState('');
  const dropdownRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  // Нормализуем value в массив для мультиселекта
  const selectedIds = useMemo(() => {
    if (!value) return [];
    if (Array.isArray(value)) return value.filter(Boolean);
    return value ? [value] : [];
  }, [value]);

  // Находим выбранные станции для отображения
  const selectedStations = useMemo(() => {
    if (selectedIds.length === 0) return [];
    const result: Array<{ station: { id: string; name: string }; line: MetroLineOption }> = [];
    for (const id of selectedIds) {
      for (const line of lines) {
        for (const st of line.stations || []) {
          if (st.id === id) {
            result.push({ station: st, line });
            break;
          }
        }
      }
    }
    return result;
  }, [selectedIds, lines]);

  // Фильтрация по подстроке названия станции или линии
  const filtered = useMemo(() => {
    const q = query.toLowerCase().trim();
    if (!q) return lines;
    return lines
      .map((line) => {
        const matchedStations = (line.stations || []).filter(
          (st) => st.name.toLowerCase().includes(q) || line.name.toLowerCase().includes(q)
        );
        if (matchedStations.length === 0) return null;
        return { ...line, stations: matchedStations };
      })
      .filter(Boolean) as MetroLineOption[];
  }, [lines, query]);

  // Клик вне — закрыть
  useEffect(() => {
    const handler = (e: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target as Node)) {
        setIsOpen(false);
        setQuery('');
      }
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  // Фокус на поле поиска при открытии
  useEffect(() => {
    if (isOpen && inputRef.current) {
      inputRef.current.focus();
    }
  }, [isOpen]);

  const handleSelect = (stationId: string) => {
    if (multiple) {
      const isSelected = selectedIds.includes(stationId);
      const next = isSelected
        ? selectedIds.filter((id) => id !== stationId)
        : [...selectedIds, stationId];
      onChange(next);
    } else {
      onChange(stationId);
      setIsOpen(false);
      setQuery('');
    }
  };

  const handleClear = (e: React.MouseEvent) => {
    e.stopPropagation();
    onChange(multiple ? [] : '');
    setIsOpen(false);
    setQuery('');
  };

  const handleRemoveStation = (e: React.MouseEvent, stationId: string) => {
    e.stopPropagation();
    const next = selectedIds.filter((id) => id !== stationId);
    onChange(next.length > 0 ? next : (multiple ? [] : ''));
  };

  const totalStations = filtered.reduce((acc, l) => acc + (l.stations?.length || 0), 0);
  const hasValue = selectedIds.length > 0;

  return (
    <div className={`relative ${className}`} ref={dropdownRef}>
      {/* Кнопка-триггер */}
      <button
        type="button"
        onClick={() => { setIsOpen(!isOpen); setQuery(''); }}
        className={`
          w-full px-6 py-4 border border-[#e2e4e9] rounded-xl text-base text-left
          flex items-center justify-between
          hover:bg-[#f6f8fa] transition-colors cursor-pointer
          ${compact ? 'px-3 py-[10px] rounded-[10px] text-[14px]' : 'px-6 py-4 rounded-xl text-base'}
          ${isOpen ? '' : ''}
        `}
        style={isOpen ? { borderColor: accent, boxShadow: `0 0 0 1px ${accent}` } : undefined}
      >
        <span className={hasValue ? 'text-[#0a0d14] flex-1 min-w-0' : 'text-[#868c98]'}>
          {hasValue ? (
            multiple && selectedStations.length > 1 ? (
              <span className="truncate block">{selectedStations.length} станций выбрано</span>
            ) : selectedStations.length === 1 ? (
              <span className="flex items-center gap-2">
                {selectedStations[0].line.hexColor && (
                  <span
                    className="inline-block w-3 h-3 rounded-full flex-shrink-0"
                    style={{ backgroundColor: selectedStations[0].line.hexColor }}
                  />
                )}
                <span className="truncate">{selectedStations[0].station.name}</span>
              </span>
            ) : placeholder
          ) : placeholder}
        </span>
        <span className="flex items-center gap-1 flex-shrink-0">
          {hasValue && (
            <span onClick={handleClear} className="text-[#868c98] hover:text-red-500 p-0.5">
              <XIcon className="w-4 h-4" />
            </span>
          )}
          <ChevronDownIcon className={`w-4 h-4 text-[#525866] transition-transform ${isOpen ? 'rotate-180' : ''}`} />
        </span>
      </button>

      {/* Выбранные станции (тэги) — показываем при multiple и нескольких выбранных */}
      {multiple && selectedStations.length > 0 && (
        <div className="flex flex-wrap gap-1 mt-1">
          {selectedStations.map(({ station, line }) => (
            <span
              key={station.id}
              className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs border"
              style={{ color: accent, borderColor: `${accent}40`, backgroundColor: `${accent}10` }}
            >
              {line.hexColor && (
                <span
                  className="inline-block w-2 h-2 rounded-full flex-shrink-0"
                  style={{ backgroundColor: line.hexColor }}
                />
              )}
              {station.name}
              <button
                type="button"
                onClick={(e) => handleRemoveStation(e, station.id)}
                className="hover:text-red-500 ml-0.5"
              >
                <XIcon className="w-3 h-3" />
              </button>
            </span>
          ))}
        </div>
      )}

      {/* Выпадающий список */}
      {isOpen && (
        <div className="absolute top-full left-0 right-0 mt-1 bg-white border border-[#e2e4e9] rounded-xl shadow-lg z-50 max-h-80 overflow-hidden">
          {/* Поиск */}
          <div className="p-3 border-b border-[#e2e4e9]">
            <div className="relative">
              <SearchIcon className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[#868c98]" />
              <input
                ref={inputRef}
                type="text"
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                placeholder="Поиск станции..."
                className="w-full pl-10 pr-10 py-2 text-base border border-[#e2e4e9] rounded-lg focus:outline-none focus:ring-1"
                style={{ '--tw-ring-color': accent } as React.CSSProperties}
                onFocus={(e) => { e.target.style.borderColor = accent; }}
                onBlur={(e) => { e.target.style.borderColor = '#e2e4e9'; }}
              />
              {query && (
                <button
                  type="button"
                  onClick={() => { setQuery(''); inputRef.current?.focus(); }}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-[#868c98] hover:text-[#525866]"
                >
                  <XIcon className="w-4 h-4" />
                </button>
              )}
            </div>
          </div>

          {/* Результаты */}
          <div className="max-h-60 overflow-y-auto">
            {totalStations === 0 && (
              <div className="px-4 py-3 text-center text-[#868c98] text-sm">
                Ничего не найдено
              </div>
            )}
            {filtered.map((line) => (
              <div key={line.id}>
                {/* Заголовок линии */}
                <div className="px-4 py-1.5 text-xs font-semibold text-[#525866] bg-[#f6f8fa] flex items-center gap-2 sticky top-0">
                  {line.hexColor && (
                    <span
                      className="inline-block w-2.5 h-2.5 rounded-full flex-shrink-0"
                      style={{ backgroundColor: line.hexColor }}
                    />
                  )}
                  {line.name}
                </div>
                {/* Станции */}
                {(line.stations || []).map((st) => {
                  const isSelected = selectedIds.includes(st.id);
                  return (
                    <button
                      key={st.id}
                      type="button"
                      onClick={() => handleSelect(st.id)}
                      className={`w-full px-6 py-2 text-left text-sm hover:bg-[#f6f8fa] transition-colors flex items-center gap-2 ${
                        isSelected ? 'font-medium' : 'text-[#0a0d14]'
                      }`}
                      style={isSelected ? { backgroundColor: `${accent}10`, color: accent } : undefined}
                    >
                      {multiple && (
                        <span
                          className={`w-4 h-4 rounded border flex-shrink-0 flex items-center justify-center ${
                            isSelected ? '' : 'border-[#d1d5db]'
                          }`}
                          style={isSelected ? { backgroundColor: accent, borderColor: accent } : undefined}
                        >
                          {isSelected && (
                            <svg className="w-3 h-3 text-white" viewBox="0 0 12 12" fill="none">
                              <path d="M2 6l3 3 5-5" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                            </svg>
                          )}
                        </span>
                      )}
                      {st.name}
                    </button>
                  );
                })}
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};
